import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Check, X, Printer, Pencil, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { money, outletNames, statusLabels, statusTone, formatDate } from "../lib/format";
import { useOutlets } from "../lib/useOutlets";
import { PageIntro, PanelHead, Modal, Field, SelectField, Badge, Pagination } from "../components/UI";
import { usePagination } from "../lib/usePagination";
import { BulkUploadDialog } from "../components/BulkUpload";
import { useAuth } from "../context/AuthContext";
import { printPurchaseOrder } from "../lib/printDocs";
import { ItemPicker } from "../components/ItemPicker";

const TEMPLATE = {
  headers: ["po_ref", "supplier", "outlet_code", "item_sku", "qty", "price", "notes"],
  example: [
    ["A1", "PT Boga Utama", "kitchen", "INV-0001", "20", "185000", "Weekly restock"],
    ["A1", "PT Boga Utama", "kitchen", "INV-0006", "10", "220000", ""],
    ["A2", "PT Pernod Ricard", "bar", "INV-0004", "12", "450000", ""],
  ],
};

export default function OrdersPage() {
  const { user } = useAuth();
  const outletsList = useOutlets();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null); // "new" | "upload" | "edit" | {mode:"cancel",po}
  const [editingId, setEditingId] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [supplier, setSupplier] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [outletCode, setOutletCode] = useState("kitchen");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ item_id: "", qty: "1", price: "0" }]);
  const [saving, setSaving] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [forceReason, setForceReason] = useState("");

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const canCreate = ["admin", "purchasing"].includes(user?.role);
  const canApprove = ["admin", "finance"].includes(user?.role);
  const canCancel = user?.role === "admin";
  const canForceClose = ["admin", "purchasing"].includes(user?.role);

  const openNew = () => {
    setEditingId(null);
    setSupplierId("");
    setSupplier("");
    setPaymentTerms("");
    setOutletCode("kitchen");
    setNotes("");
    setLines([{ item_id: "", qty: "1", price: "0" }]);
    setModal("new");
  };

  const openEdit = (po) => {
    if (po.status !== "waiting_approval") {
      toast.error("Only PO in waiting approval can be edited");
      return;
    }
    setEditingId(po.id);
    const matched = suppliers.find((s) => s.name === po.supplier);
    setSupplierId(matched?.id || "");
    setSupplier(po.supplier || "");
    setPaymentTerms(po.payment_terms || matched?.payment_terms || "");
    setOutletCode(po.outlet_code || "kitchen");
    setNotes(po.notes || "");
    setLines(
      (po.items || []).map((l) => ({
        item_id: l.item_id,
        qty: String(l.qty),
        price: String(l.price),
      }))
    );
    setModal("edit");
  };

  const onPickSupplier = (id) => {
    setSupplierId(id);
    const s = suppliers.find((x) => x.id === id);
    if (s) {
      setSupplier(s.name);
      // Only auto-fill payment terms if the field is currently empty (don't clobber a manual edit)
      if (!paymentTerms) setPaymentTerms(s.payment_terms || "");
    }
  };

  const addLine = () => setLines([...lines, { item_id: "", qty: "1", price: "0" }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) => {
    const next = [...lines];
    next[idx] = { ...next[idx], [field]: value };
    if (field === "item_id") {
      const it = items.find((i) => i.id === value);
      if (it) next[idx].price = String(it.cost);
    }
    setLines(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!supplier) return toast.error("Supplier is required");
    const cleanLines = lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (cleanLines.length === 0) return toast.error("Add at least 1 item");
    setSaving(true);
    try {
      const payload = {
        supplier,
        outlet_code: outletCode,
        notes,
        payment_terms: paymentTerms,
        items: cleanLines.map((l) => {
          const it = items.find((i) => i.id === l.item_id);
          return {
            item_id: l.item_id,
            name: it.name,
            qty: Number(l.qty),
            unit: it.unit,
            price: Number(l.price),
          };
        }),
      };
      if (editingId) {
        await api.put(`/orders/${editingId}`, payload);
        toast.success("PO updated successfully");
      } else {
        await api.post("/orders", payload);
        toast.success("PO created successfully");
      }
      setModal(null);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/orders/${id}/approve`);
      toast.success("PO approved");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openCancel = (po) => {
    setCancelReason("");
    setModal({ mode: "cancel", po });
  };

  const openForceClose = (po) => {
    setForceReason("");
    setModal({ mode: "force-close", po });
  };

  const submitForceClose = async (e) => {
    e.preventDefault();
    const reason = forceReason.trim();
    if (!reason) return toast.error("Force-close reason is required");
    if (reason.length < 4) return toast.error("Reason must be at least 4 characters");
    setSaving(true);
    try {
      await api.post(`/orders/${modal.po.id}/force-close`, { reason });
      toast.success(`PO ${modal.po.po_number} force-closed`);
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const submitCancel = async (e) => {
    e.preventDefault();
    const reason = cancelReason.trim();
    if (!reason) return toast.error("Cancellation reason is required");
    if (reason.length < 4) return toast.error("Reason must be at least 4 characters");
    setSaving(true);
    try {
      await api.post(`/orders/${modal.po.id}/cancel`, { reason });
      toast.success(`PO ${modal.po.po_number} cancelled`);
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const printPO = (po) => {
    const supplier = suppliers.find((s) => s.name === po.supplier);
    printPurchaseOrder(po, supplier);
  };

  const linesTotal = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.price || 0), 0);

  const filteredOrders = statusFilter
    ? orders.filter((o) => o.status === statusFilter)
    : orders;
  const pager = usePagination(filteredOrders, 100, statusFilter || "all");

  return (
    <>
      <PageIntro
        eyebrow="Procurement · workflow"
        title="Purchase order"
        subtitle="Track procurement from request to received."
        testid="orders-title"
        action={
          <div className="action-cluster">
            {canCreate && (
              <button
                data-testid="orders-upload-button"
                className="secondary-button"
                onClick={() => setModal("upload")}
              >
                <Upload size={16} /> Upload CSV
              </button>
            )}
            {canCreate && (
              <button
                data-testid="create-po-button"
                className="primary-button"
                onClick={openNew}
              >
                <Plus size={17} /> Create new PO
              </button>
            )}
          </div>
        }
      />
      <section className="panel">
        <PanelHead title="Purchase order list" detail={`${filteredOrders.length} PO recorded`} />
        {statusFilter && (
          <div className="toolbar">
            <button
              type="button"
              className="chip small"
              data-testid="po-clear-status-filter"
              onClick={() => setSearchParams({})}
              title="Hapus filter status"
            >
              Filter: {statusLabels[statusFilter] || statusFilter} <X size={12} />
            </button>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>PO No.</th>
                <th>Supplier</th>
                <th>Outlet</th>
                <th>Item</th>
                <th>Total</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 40 }}>No PO yet.</td></tr>
              )}
              {pager.pageItems.map((o) => {
                const totalQty = o.items.reduce((s, l) => s + Number(l.qty), 0);
                const recvQty = o.items.reduce((s, l) => s + Number(l.received_qty || 0), 0);
                const pct = totalQty > 0 ? Math.round((recvQty / totalQty) * 100) : 0;
                return (
                  <tr key={o.id} data-testid={`po-row-${o.id}`}>
                    <td>
                      <strong>{o.po_number}</strong>
                    </td>
                    <td>{o.supplier}</td>
                    <td>{outletNames[o.outlet_code] || o.outlet_code}</td>
                    <td>
                      {o.items.length} lines
                      {o.status !== "waiting_approval" && o.status !== "cancelled" && (
                        <small>
                          Received: {recvQty}/{totalQty} ({pct}%)
                        </small>
                      )}
                    </td>
                    <td><strong>{money(o.total)}</strong></td>
                    <td>
                      <Badge tone={statusTone[o.status]}>{statusLabels[o.status] || o.status}</Badge>
                    </td>
                    <td><small>{formatDate(o.created_at)}</small></td>
                    <td>
                      {o.status === "waiting_approval" && canApprove && (
                        <button
                          data-testid={`approve-${o.id}`}
                          className="small-button success"
                          onClick={() => approve(o.id)}
                        >
                          <Check size={12} /> Approve
                        </button>
                      )}
                      {(o.status === "waiting_approval" || (user?.role === "admin" && !["received", "cancelled"].includes(o.status))) && canCreate && (
                        <button
                          data-testid={`po-edit-${o.id}`}
                          className="small-button"
                          style={{ marginLeft: 6 }}
                          onClick={() => openEdit(o)}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                      {canCancel && o.status !== "cancelled" && o.status !== "received" && o.status !== "closed" && (
                        <button
                          data-testid={`po-cancel-${o.id}`}
                          className="small-button"
                          style={{ marginLeft: 6 }}
                          onClick={() => openCancel(o)}
                        >
                          <X size={12} /> Cancel
                        </button>
                      )}
                      {canForceClose && o.status === "partial" && (
                        <button
                          data-testid={`po-force-close-${o.id}`}
                          className="small-button"
                          style={{ marginLeft: 6 }}
                          onClick={() => openForceClose(o)}
                          title="Force close this partially received PO. Un-received qty will be forfeited."
                        >
                          <Lock size={12} /> Force close
                        </button>
                      )}
                      <button
                        data-testid={`po-print-${o.id}`}
                        className="small-button"
                        style={{ marginLeft: 6 }}
                        onClick={() => printPO(o)}
                        title="Print PO"
                      >
                        <Printer size={12} /> Print
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="orders-pagination" />
      </section>

      {(modal === "new" || modal === "edit") && (
        <Modal
          title={modal === "edit" ? "Edit purchase order" : "Create purchase order"}
          onClose={() => { setModal(null); setEditingId(null); }}
        >
          <form className="form-grid" onSubmit={submit}>
            <label className="field">
              <span>Supplier</span>
              <select
                data-testid="po-supplier-select"
                value={supplierId}
                onChange={(e) => onPickSupplier(e.target.value)}
              >
                <option value="">-- Pick from catalog --</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code}) · lead {s.lead_time_days}d
                  </option>
                ))}
              </select>
            </label>
            <Field label="Or custom supplier name" testid="po-supplier-input" value={supplier} onChange={setSupplier} placeholder="PT Boga Utama" />
            <SelectField
              label="Destination outlet"
              testid="po-outlet-select"
              value={outletCode}
              onChange={setOutletCode}
              options={outletsList.map((o) => ({ value: o.code, label: o.name }))}
            />
            <Field
              label="Payment terms"
              testid="po-payment-terms-input"
              value={paymentTerms}
              onChange={setPaymentTerms}
              placeholder="Net 14, COD, etc."
            />
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Notes (optional)</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Weekly restock..." />
            </label>
            <div style={{ gridColumn: "1/-1" }}>
              <p className="eyebrow">Line items</p>
              <div className="line-editor">
                {lines.map((l, idx) => (
                  <div className="line-row" key={idx}>
                    <ItemPicker
                      items={items}
                      value={l.item_id}
                      onChange={(id) => updateLine(idx, "item_id", id)}
                      testid={`po-line-item-${idx}`}
                      placeholder="Search item by name or SKU..."
                    />
                    <input
                      data-testid={`po-line-qty-${idx}`}
                      type="number"
                      value={l.qty}
                      onChange={(e) => updateLine(idx, "qty", e.target.value)}
                      placeholder="Qty"
                    />
                    <input
                      data-testid={`po-line-price-${idx}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.price}
                      onChange={(e) => updateLine(idx, "price", e.target.value)}
                      placeholder="Price"
                    />
                    <button type="button" className="icon-button" onClick={() => removeLine(idx)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button type="button" data-testid="po-add-line" className="chip small" onClick={addLine}>
                  <Plus size={12} /> Add row
                </button>
              </div>
              <div className="line-total">Total: <strong>{money(linesTotal)}</strong></div>
            </div>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => { setModal(null); setEditingId(null); }}>Cancel</button>
              <button data-testid="po-save-button" className="primary-button" type="submit" disabled={saving}>
                <Check size={16} /> {saving ? "Saving..." : editingId ? "Update PO" : "Save PO"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "upload" && (
        <BulkUploadDialog
          title="Upload purchase order (CSV)"
          endpoint="/orders/bulk-upload"
          templateName="purchase_order_template.csv"
          templateHeaders={TEMPLATE.headers}
          templateExample={TEMPLATE.example}
          instructions={
            <>
              Group rows with the same <b>po_ref</b> value to create a single PO.
              The <b>item_sku</b> column must match an SKU in the item master.
            </>
          }
          onClose={() => setModal(null)}
          onSuccess={load}
          testid="orders-upload"
        />
      )}

      {modal && typeof modal === "object" && modal.mode === "cancel" && (
        <Modal
          title={`Cancel PO ${modal.po.po_number}`}
          onClose={() => setModal(null)}
        >
          <form className="form-grid" onSubmit={submitCancel}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              This action cannot be undone. Please provide a reason so it is recorded on the PO history.
            </p>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Cancellation reason</span>
              <textarea
                data-testid="po-cancel-reason-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. supplier unavailable, duplicate order, wrong quantity..."
                rows={4}
                autoFocus
                required
              />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>
                Keep PO
              </button>
              <button
                data-testid="po-cancel-confirm-button"
                className="primary-button danger"
                type="submit"
                disabled={saving}
              >
                <X size={16} /> {saving ? "Cancelling..." : "Confirm cancel"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal && typeof modal === "object" && modal.mode === "force-close" && (
        <Modal
          title={`Force close PO ${modal.po.po_number}`}
          onClose={() => setModal(null)}
        >
          <form className="form-grid" onSubmit={submitForceClose}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              This PO has been <b>partially received</b>. Force-closing marks it as done and forfeits
              any remaining quantity. Received stock is retained. Provide a reason for the audit log.
            </p>
            <div className="table-wrap" style={{ gridColumn: "1/-1", maxHeight: 220, overflow: "auto" }}>
              <table>
                <thead>
                  <tr><th>Item</th><th>Account</th><th>Ordered</th><th>Received</th><th>Forfeited</th></tr>
                </thead>
                <tbody>
                  {(modal.po.items || []).map((l, i) => {
                    const ordered = Number(l.qty || 0);
                    const received = Number(l.received_qty || 0);
                    const forfeit = Math.max(0, ordered - received);
                    return (
                      <tr key={i}>
                        <td><strong>{l.name}</strong></td>
                        <td>
                          {l.account_code ? (
                            <>
                              <span className="badge badge-blue">{l.account_code}</span>
                              {l.account_name ? <small style={{ display: "block", color: "var(--muted)" }}>{l.account_name}</small> : null}
                            </>
                          ) : <small style={{ color: "var(--amber)" }}>—</small>}
                        </td>
                        <td>{ordered} {l.unit || ""}</td>
                        <td>{received} {l.unit || ""}</td>
                        <td>
                          {forfeit > 0 ? (
                            <span className="amber-text"><strong>{forfeit}</strong> {l.unit || ""}</span>
                          ) : <small>—</small>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Force-close reason</span>
              <textarea
                data-testid="po-force-close-reason-input"
                value={forceReason}
                onChange={(e) => setForceReason(e.target.value)}
                placeholder="e.g. supplier out of stock for remainder, delivery cut short..."
                rows={3}
                autoFocus
                required
              />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>
                Keep open
              </button>
              <button
                data-testid="po-force-close-confirm-button"
                className="primary-button"
                type="submit"
                disabled={saving}
              >
                <Lock size={16} /> {saving ? "Closing..." : "Confirm force close"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
